import React, {
    useContext
} from 'react';
import {
    Calendar, UserIcon
} from 'lucide-react';
import {
    Tooltip, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import KBTooltipContent from './KBTooltipContent';
import {
    KBContext
} from '../knowledgeBuilderContext/KBGlobalContext';
import {
    CustomTextSkeleton
} from 'shared/components/ui/text-skeleton';
import KBTooltipWrapper from './TooltipWrapper';
import {
    COLLECTION_PROCESS_STATUS
} from '../config/valueMapping';

const CollectionNamePlate = ({
    collectionName, createdBy = '--', createdAt = '--', metaMap, collectionId
}) => {
    const {
        collectionLoading, l1Status, l2Status, tableHandler
    } = useContext(KBContext);

    const {
        setStatusFilter
    } = tableHandler || {};

    // Calculate file counts
    const fileCounts = React.useMemo(() => {
        if (!metaMap) {
            return {
                total: 0,
                uploading: 0,
                successful: 0,
                failed: 0
            };
        }

        let total = 0,
            uploading = 0,
            successful = 0,
            failed = 0;

        for (const [, metadata] of metaMap.entries()) {
            if (metadata.type === 'file') {
                total++;
                const l1 = (metadata.status?.status || '').toLowerCase();

                if (l1 === 'processing' || l1 === 'checking') {
                    uploading++;
                } else if (l1 === 'completed' || l1 === 'success') {
                    successful++;
                } else if (l1 === 'failed' || l1 === 'error' || l1 === 'cancelled') {
                    failed++;
                }
            }
        }

        return {
            total,
            uploading,
            successful,
            failed
        };
    }, [metaMap]);

    const displayStatus = (status) => {
        if (COLLECTION_PROCESS_STATUS[status]) {
            return COLLECTION_PROCESS_STATUS[status];
        } else if (status === 'success') {
            return 'Completed';
        } else {
            return 'Unknown';
        }
    };

    const getBadgeBackground = (status) => {
        if (status === 'completed' || status === 'success') {
            return 'bg-green-50 dark:bg-green-900/20';
        } else if (status === 'processing') {
            return 'bg-yellow-50 dark:bg-yellow-900/20';
        } else if (status === 'failed' || status === 'error') {
            return 'bg-red-50 dark:bg-red-900/20';
        } else {
            return 'bg-gray-50 dark:bg-gray-900/20';
        }
    };

    const getDotColor = (status) => {
        if (status === 'completed' || status === 'success') {
            return 'bg-green-500 dark:bg-green-400';
        } else if (status === 'processing') {
            return 'bg-yellow-500 dark:bg-yellow-400';
        } else if (status === 'failed' || status === 'error') {
            return 'bg-red-500 dark:bg-red-400';
        } else {
            return 'bg-gray-500 dark:bg-gray-400';
        }
    };

    const getTextColor = (status) => {
        if (status === 'completed' || status === 'success') {
            return 'text-green-700 dark:text-green-400';
        } else if (status === 'processing') {
            return 'text-yellow-700 dark:text-yellow-400';
        } else if (status === 'failed' || status === 'error') {
            return 'text-red-700 dark:text-red-400';
        } else {
            return 'text-gray-700 dark:text-gray-400';
        }
    };

    return (
        <div className="pt-4 px-6 pb-4">
            <div className="flex items-center justify-between">
                <div className="flex flex-col">
                    <h2 className="text-2xl text-foreground font-bold">
                        <CustomTextSkeleton
                            className="h-[2rem]"
                            width={300}
                            loading={collectionLoading}>
                            {collectionName || <span className='italic text-muted-foreground'>Unnamed Collection</span>}
                        </CustomTextSkeleton>
                    </h2>

                    <div className="flex items-center gap-4 mt-2">
                        <CustomTextSkeleton
                            className="h-[1.25rem]"
                            width={80}
                            loading={collectionLoading}>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <UserIcon size={14} className="text-muted-foreground" />
                                            {createdBy}
                                        </span>
                                    </TooltipTrigger>
                                    {createdBy === '--' ? null : <KBTooltipContent tooltipText={`This collection was created by ${createdBy}`}></KBTooltipContent>}
                                </Tooltip>
                            </TooltipProvider>
                        </CustomTextSkeleton>
                        <CustomTextSkeleton
                            className="h-[1.25rem]"
                            width={100}
                            loading={collectionLoading}>
                            <KBTooltipWrapper tooltipText={`This collection was created on ${createdAt}`}>
                                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                                    <Calendar size={14} className="text-muted-foreground" />
                                    {createdAt}
                                </div>
                            </KBTooltipWrapper>
                        </CustomTextSkeleton>
                        <CustomTextSkeleton
                            className="h-[1.25rem]"
                            width={200}
                            loading={collectionLoading}>
                            <span className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                                Collection ID: {collectionId || '...'}
                            </span>
                        </CustomTextSkeleton>
                    </div>

                    {/* Collection Summary */}
                    <div className="flex items-center gap-6 mt-3">
                        <span
                            className="text-sm font-semibold text-foreground bg-muted px-3 py-1 rounded-md cursor-pointer hover:bg-muted/80"
                            onClick={() => setStatusFilter && setStatusFilter(null)}
                        >
                            {fileCounts.total} Total Files
                        </span>
                        <span
                            className="text-sm font-semibold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/40 px-3 py-1 rounded-md cursor-pointer hover:bg-green-200 dark:hover:bg-green-900/60"
                            onClick={() => setStatusFilter && setStatusFilter('successful')}
                        >
                            {fileCounts.successful} Successful
                        </span>
                        <span
                            className="text-sm font-semibold text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 px-3 py-1 rounded-md cursor-pointer hover:bg-red-200 dark:hover:bg-red-900/60"
                            onClick={() => setStatusFilter && setStatusFilter('failed')}
                        >
                            {fileCounts.failed} Failed
                        </span>
                    </div>
                </div>

                <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                        <span className="text-xs font-semibold text-muted-foreground uppercase mb-1">L1 Status</span>
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${getBadgeBackground(l1Status)} border`}>
                            <div className={`w-2 h-2 rounded-full ${getDotColor(l1Status)}`}></div>
                            <span className={`text-sm font-medium ${getTextColor(l1Status)}`}>
                                {displayStatus(l1Status)}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col items-center">
                        <span className="text-xs font-semibold text-muted-foreground uppercase mb-1">L2 Status</span>
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${getBadgeBackground(l2Status)} border`}>
                            <div className={`w-2 h-2 rounded-full ${getDotColor(l2Status)}`}></div>
                            <span className={`text-sm font-medium ${getTextColor(l2Status)}`}>
                                {displayStatus(l2Status)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default CollectionNamePlate;